import * as React from 'react'

import { clsx } from 'clsx'

import {
  Button,
  CircleFadingArrowUpIcon,
  CoachMark,
  IconButton,
  Tooltip,
} from '@buffer-mono/popcorn'

import {
  forgetPendingPostComments,
  takePendingPostCommentsSnapshot,
  usePendingPostCommentsCount,
} from '../../../hooks/comments/pendingPostCommentsSignal'
import { postsReloadFailed } from '../hooks/useReloadPostsToTop'
import { useReorderPostsCoachmark } from './useReorderPostsCoachmark'

import styles from './PostsSidepanel.module.css'

import type { JSX } from 'react'

/** States only the action, since nothing has arrived to announce. */
const IDLE_LABEL = 'Bring posts with the newest unanswered comments to the top.'
/** Leads with what arrived, then what the click will do about it. */
const NEW_COMMENTS_LABEL =
  'New comments on other posts. Bring posts with the newest unanswered comments to the top.'
const COACHMARK_TITLE = 'New comments on other posts'
/** The title already announces the arrival, so the body is only the action. */
const COACHMARK_DESCRIPTION =
  'Reorder the post list and bring posts with the newest unanswered comments to the top.'

type ReorderPostsButtonProps = {
  /** Reloads the posts list from its first page, resolving with the result. */
  onReorder: () => Promise<unknown>
}

/**
 * Posts panel header control that reorders the list once comments have
 * arrived.
 *
 * The list holds its order while comments come in, so this is the only thing
 * that reorders it, and the only route by which a post gaining its first
 * comment reaches the panel.
 *
 * It is always here, quiet until comments arrive and then brand-coloured. A
 * control that only appeared when we had something to announce could not be
 * used to ask, and there is a reason to ask: replying re-ranks nothing, so
 * after working through a batch the order is stale with nothing arriving to
 * say so.
 *
 * A reorder drops only the comments it covered. A failure drops nothing, and a
 * comment landing while the reload runs is not in the reloaded page, so either
 * one keeps the control on screen as the way back to what it announced.
 *
 * The first time it ever appears it is explained, which is also the only time
 * an icon this quiet gets to say what it does.
 */
export function ReorderPostsButton({
  onReorder,
}: ReorderPostsButtonProps): JSX.Element {
  const commentCount = usePendingPostCommentsCount()
  const { shouldShowCoachmark, dismissCoachmark } = useReorderPostsCoachmark()

  const hasNewComments = commentCount > 0
  const label = hasNewComments ? NEW_COMMENTS_LABEL : IDLE_LABEL

  // Guards re-entry without disabling the button, which would take its tooltip
  // with it.
  const isReorderingRef = React.useRef(false)

  const handleReorder = React.useCallback(async (): Promise<void> => {
    // Acting on the control is understanding it, however the reader got here.
    dismissCoachmark()

    if (isReorderingRef.current) return
    isReorderingRef.current = true

    const covered = takePendingPostCommentsSnapshot()
    try {
      const result = await onReorder()
      if (!postsReloadFailed(result)) {
        forgetPendingPostComments(covered)
      }
    } catch {
      // A failed reorder leaves the list as it was, so the tally still stands.
      // Keeping it holds the control on screen, which is the way back to the
      // comments it announced.
    } finally {
      isReorderingRef.current = false
    }
  }, [onReorder, dismissCoachmark])

  const control = (
    <IconButton
      label={label}
      variant={hasNewComments ? 'primary' : 'tertiary'}
      size="small"
      className={clsx(
        styles.reorderPostsButton,
        hasNewComments && styles.reorderPostsButtonActive,
      )}
      onClick={handleReorder}
    >
      <CircleFadingArrowUpIcon />
    </IconButton>
  )

  // The explanation names the comments that arrived, so it waits for some to
  // arrive rather than greeting every reader who opens the panel.
  if (!shouldShowCoachmark || !hasNewComments) {
    return <Tooltip content={label}>{control}</Tooltip>
  }

  // No tooltip while the explanation is up: two layers over one icon is the
  // noise this control exists to avoid.
  //
  // Uncontrolled, so the hover card closes on pointer-out and returns on hover.
  // `dismissTimeout` would turn one of those closes into a permanent dismissal,
  // so it is off and only the buttons below retire it.
  return (
    <CoachMark defaultOpen dismissTimeout={null} onDismiss={dismissCoachmark}>
      <CoachMark.Overlay>
        {control}
        <CoachMark.Spotlight size="small" />
      </CoachMark.Overlay>
      {/* "Got it" already dismisses, so the corner icon would be a second way
          to do the same thing on a popover this small. */}
      <CoachMark.Content showCloseIcon={false}>
        <CoachMark.Title>{COACHMARK_TITLE}</CoachMark.Title>
        <CoachMark.Description>{COACHMARK_DESCRIPTION}</CoachMark.Description>
        <CoachMark.Footer>
          <CoachMark.Dismiss>
            <Button variant="tertiary">Got it</Button>
          </CoachMark.Dismiss>
          <Button variant="primary" onClick={handleReorder}>
            Reorder List
          </Button>
        </CoachMark.Footer>
      </CoachMark.Content>
    </CoachMark>
  )
}
